import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../utils/cn';

export const TooltipProvider = TooltipPrimitive.Provider;

/**
 * A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.
 * @see {@link https://stylus.design/tooltip}
 */
export const Tooltip = (props: TooltipPrimitive.TooltipProps) => (
  <TooltipPrimitive.Root delayDuration={100} {...props} />
);

export const TooltipTrigger = ({
  asChild = true,
  ...props
}: {
  asChild?: boolean;
} & React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>) => (
  <TooltipPrimitive.Trigger asChild={asChild} {...props} />
);

const tooltipVariants = cva(
  'font-sans animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden bg-slate-800 text-slate-100',
  {
    variants: {
      variant: {
        default: 'h-9 flex items-center rounded-lg px-4 text-sm font-medium',
        compact: 'text-xs px-2 py-1 flex items-center rounded',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipVariants> {}

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 10, variant = 'default', ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(tooltipVariants({ variant }), className)}
    {...props}
  />
));

export const TooltipArrow = TooltipPrimitive.Arrow;
export const TooltipPortal = TooltipPrimitive.Portal;
